import { logger, safeError } from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { findAll, loginUser } from '../services/Service';
import { RootState } from '../app/store';
import {
  clearToken,
  loadingError,
  onLogin,
  onLogout,
  setRoles,
  setToken,
  visibleError,
  setLinkPermisos,
  setPermisos,
  setAuth,
  urlRedirect,
} from '../context/Auth/UserLoginReducer';
import { useAuthContext } from '../context/Auth/AuthProvider';

export const useAuth = () => {
  const dispatch = useDispatch();
  const {
    usuarioLogin,
    isAuth,
    errors,
    error,
    redirect,
    permisos,
    linkPermisos,
  } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const handlerLogin = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    try {
      logger.log('INICIANDO LOGIN');
      const response = await loginUser({ username, password });
      const token = response.data.token;
      const claims = JSON.parse(window.atob(token.split('.')[1]));
      handlePermisos(claims.authorities);

      localStorage.setItem('roles', claims.authorities);
      dispatch(setRoles(true));
      localStorage.setItem('token', `Bearer ${token}`);
      dispatch(setToken(`Bearer ${token}`));

      // getUsuarioByUsername();
      await findAll(`usuarios/username`)
        .then((response) => {
          localStorage.setItem(
            'login',
            JSON.stringify({
              isAuth: true,
              usuario: response.data,
            })
          );
          dispatch(onLogin(response.data));
          if (redirect) {
            navigate(redirect);
          } else {
            navigate('/consultaSolicitudes');
          }
        })
        .catch((error) => {
          logger.log(error);
        });
    } catch (error: any) {
      if (error.response?.status === 401) {
        const errors = [error.response.data.message];
        dispatch(loadingError(errors));
      } else if (error.response?.status === 403) {
        dispatch(loadingError(error.response.data.errors));
      } else {
        throw error;
      }
    }
  };

  const { isAuthenticated, checkAuth } = useAuthContext();

  const handleCognitoAuth = async () => {
    logger.log('[HANDLE-COGNITO-AUTH] ===== START =====');
    const result = await checkAuth();
    logger.log('[HANDLE-COGNITO-AUTH] checkAuth result:', result);

    if (result === true) {
      logger.log('[HANDLE-COGNITO-AUTH] => autenticado, navegando a bandejaTrabajo');
      navigate(redirect || '/bandejaTrabajo');
    } else if (result === false) {
      logger.log('[HANDLE-COGNITO-AUTH] => no autenticado, navegando a /login');
      navigate('/login');
    } else {
      logger.log('[HANDLE-COGNITO-AUTH] => result null, checkAuth redirigiendo a Cognito');
    }
    logger.log('[HANDLE-COGNITO-AUTH] ===== END =====');
  };

  const checkAuthStatus = async () => {
    try {
      const response = await findAll(`auth/status`);
      if (response?.data?.authenticated) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const showError = (load: boolean) => {
    dispatch(visibleError(load));
  };

  const handlerLogout = async () => {
    try {
      logger.log('Iniciando logout...');

      // 1. Limpiar estado de Redux inmediatamente
      dispatch(onLogout());
      dispatch(clearToken());
      dispatch(urlRedirect(null));
      dispatch(setAuth(false));
      dispatch(setRoles(false));

      // 2. Limpiar localStorage y sessionStorage
      localStorage.removeItem('token');
      localStorage.removeItem('login');
      localStorage.removeItem('roles');
      sessionStorage.removeItem('authStatus');
      sessionStorage.removeItem('silentCheckAttempted');

      logger.log(
        'Estado local limpiado. Redirigiendo al logout del backend...'
      );

      // 4. Redirigir al endpoint de logout del backend
      // El backend se encargará de redirigir a Cognito y manejar el callback
      const logoutUrl = `${process.env.REACT_APP_API_URL}/auth/logout`;
      window.location.href = logoutUrl;
    } catch (error) {
      logger.error('Error durante logout:', safeError(error));
      // Solo en caso de error, limpiar cookies manualmente como fallback
      const clearCookies = () => {
        const cookies = [
          'JSESSIONID',
          'access_token',
          'id_token',
          'refresh_token',
        ];
        cookies.forEach((cookieName) => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
      };
      clearCookies();
      window.location.replace('/login');
    }
  };
  const handleRedirect = (url: string | null) => {
    dispatch(urlRedirect(url));
  };

  const handlePermisos = (
    authorities: any,
    shouldLogoutOnError = false
  ) => {
    logger.log('handlePermisos', authorities);
    if (permisos && permisos.length > 0) {
      logger.log('Permissions already loaded from Redux:', permisos);
      logger.log('Links already loaded from Redux:', linkPermisos);
      return; // No necesita procesar, ya están cargados
    }
    const rolesPoncho = JSON.parse(authorities);
    if (Object.keys(rolesPoncho).length !== 0) {
      const auth = JSON.parse(rolesPoncho[0].authority);

      const nombres: string[] = [];
      const links: string[] = [];
      auth &&
        auth.menu.forEach((rol: { name: any; sub: any[] }) => {
          nombres.push(rol.name);
          if (rol.sub) {
            rol.sub.forEach((rolSub) => {
              nombres.push(rolSub.acro);
              links.push(rolSub.link);
            });
          }
        });

      dispatch(setPermisos(nombres));
      dispatch(setLinkPermisos(links));
    } else {
      if (shouldLogoutOnError) {
        logger.log('Haciendo logout por falta de permisos');
        handlerLogout();
      } else {
        // Establecer permisos vacíos pero no hacer logout
        dispatch(setPermisos([]));
        dispatch(setLinkPermisos([]));
      }
    }
  };

  const getUsuarioLogueado = () => {
    const login = JSON.parse(localStorage.getItem('login') || '{}');
    return login?.usuario;
  };

  return {
    login: {
      usuarioLogin,
      isAuth,
    },
    handlerLogin,
    handlerLogout,
    showError,
    handlePermisos,
    getUsuarioLogueado,
    handleRedirect,
    handleCognitoAuth,
    checkAuthStatus,
    isAuth,
    errors,
    error,
    redirect,
  };
};
